// src/pages/offlineSales/terminal/hooks/useOfflineSalesTerminal.js
import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../../hooks/useAuth';
import { useAlert } from '../../../../contexts/AlertContext';
import { getShop } from '../../../../services/api/shops';
import { getActiveSession } from '../../../../services/api/offlineSales/cashierSessions';
import { createQuickSaleOrder, getSmsCapability } from '../../../../services/api/offlineSales/orders';
import { getSettings } from '../../../../services/api/offlineSales/settings';
import { getPaymentMethods } from '../../../../services/api/offlineSales/paymentMethods';
import { getMyWarehouses } from '../../../../services/api/warehouses';
import { getStorePickupOrderForPos, completeStorePickupOrderFromPos } from '../../../../services/api/storePickup';

// One shared Audio instance, rewound on each play so rapid adds/scans each beep
let addToCartAudio = null;
const playAddToCartSound = () => {
  try {
    if (!addToCartAudio) addToCartAudio = new Audio('/assets/mp3/failed.mp3');
    addToCartAudio.currentTime = 0;
    addToCartAudio.play().catch(() => {}); // ignore autoplay/decoding errors
  } catch {
    // Audio not supported — ignore
  }
};

const useOfflineSalesTerminal = () => {
  const { shopId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const storePickupOrderId = searchParams.get('storePickupOrderId');
  const { success, error, info } = useAlert();

  // ─── Core state ───
  const [shop, setShop] = useState(null);
  const [session, setSession] = useState(null);
  const [settings, setSettings] = useState(null);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [customer, setCustomer] = useState(null); // null = Cash Sales
  const [cashSalesCustomer, setCashSalesCustomer] = useState(null);
  const [warehouses, setWarehouses] = useState([]);
  const [warehouseId, setWarehouseId] = useState(null);
  const [loading, setLoading] = useState(true);

  // ─── Cart state ───
  const [cartItems, setCartItems] = useState([]);
  const [orderDiscount, setOrderDiscount] = useState(0);
  const [loyaltyPointsToRedeem, setLoyaltyPointsToRedeem] = useState(0);

  // ─── Store pickup order being completed (edit mode) ───
  // { id, orderNumber, paymentRecords } — paymentRecords are the order's existing payments still kept
  const [storePickupOrder, setStorePickupOrder] = useState(null);

  // ─── Backdate (needs backdate_pos_order) — { date: YYYY-MM-DD, sessionId } applied to every sale until changed; null = today ───
  // The sale joins the cashier session that covered that date
  const [backdate, setBackdate] = useState(null);
  const saleDate = backdate?.date || null;

  // ─── Modal flags ───
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const [showLoyaltyModal, setShowLoyaltyModal] = useState(false);
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [showHoldDrawer, setShowHoldDrawer] = useState(false);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [showSessionGate, setShowSessionGate] = useState(false);

  // ─── Last completed order (for receipt) ───
  const [completedOrder, setCompletedOrder] = useState(null);

  // ─── Hold orders ───
  const [holdOrders, setHoldOrders] = useState([]);

  // ─── Submission state ───
  const [submitting, setSubmitting] = useState(false);
  const [smsEnabled, setSmsEnabled] = useState(false);

  // ─── Initialise ───
  useEffect(() => {
    const init = async () => {
      try {
        setLoading(true);
        const [shopData, sessionData, settingsData, methodsData, smsCapData, myWarehouses] = await Promise.all([
          getShop(shopId),
          getActiveSession(shopId),
          getSettings(),
          getPaymentMethods().catch(() => []),
          getSmsCapability().catch(() => ({ smsEnabled: false })),
          getMyWarehouses().catch(() => ({ warehouses: [], defaultWarehouseId: null })),
        ]);

        setShop(shopData);
        setSession(sessionData.session || null);
        setSettings(settingsData);
        setPaymentMethods(Array.isArray(methodsData) ? methodsData : []);
        setSmsEnabled(smsCapData.smsEnabled || false);

        // Only the cashier's assigned warehouses; preselect their default, or the only one
        const wList = Array.isArray(myWarehouses?.warehouses) ? myWarehouses.warehouses : [];
        setWarehouses(wList);
        if (myWarehouses?.defaultWarehouseId) setWarehouseId(myWarehouses.defaultWarehouseId);
        else if (wList.length === 1) setWarehouseId(wList[0].id);

        // Check if session gate should block
        if (shopData.cashierSessionRequired && !sessionData.session) {
          setShowSessionGate(true);
        }

        setCashSalesCustomer(null);
      } catch (err) {
        error(err?.response?.data?.message || 'Failed to load terminal');
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [shopId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Open a store pickup order (?storePickupOrderId=) into the cart for completion ───
  const loadedStorePickupIdRef = useRef(null);
  useEffect(() => {
    if (loading || !storePickupOrderId || loadedStorePickupIdRef.current === storePickupOrderId) return;
    loadedStorePickupIdRef.current = storePickupOrderId;

    const loadStorePickupOrder = async () => {
      try {
        const { order, customer: orderCustomer, customerCreated } = await getStorePickupOrderForPos(storePickupOrderId);

        const usedKeys = new Set();
        setCartItems(order.items.map(item => {
          let key = `${item.productId}-${item.variationId || 'null'}`;
          if (usedKeys.has(key)) key = `${key}-${item.id}`;
          usedKeys.add(key);
          return {
            key,
            orderItemId: item.id,
            productId: item.productId,
            variationId: item.variationId || null,
            name: item.displayName,
            baseName: item.productName,
            variationLabel: item.variationName
              ? item.variationName.split(', ').map(part => part.split(': ').pop()).join(', ')
              : null,
            image: item.image || null,
            sku: item.sku || null,
            unitPrice: item.unitPrice,
            quantity: item.quantity,
            lineDiscount: item.lineDiscount || 0,
          };
        }));
        setOrderDiscount(0);
        setLoyaltyPointsToRedeem(0);
        setCustomer(orderCustomer);
        setStorePickupOrder({ id: order.id, orderNumber: order.orderNumber, paymentRecords: order.paymentRecords });

        if (customerCreated) {
          success(`New customer "${orderCustomer.name}" created and selected`, { duration: 3000 });
        }
        info(`Store pickup order #${order.orderNumber} loaded`, { duration: 3000 });
      } catch (err) {
        error(err?.response?.data?.message || 'Failed to load store pickup order');
      } finally {
        // Drop the param so a later reload doesn't re-open a completed order
        setSearchParams(prev => {
          const next = new URLSearchParams(prev);
          next.delete('storePickupOrderId');
          return next;
        }, { replace: true });
      }
    };
    loadStorePickupOrder();
  }, [loading, storePickupOrderId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Only unverified existing payments may be removed; verified ones stay on the order
  const removeStorePickupPayment = useCallback((recordId) => {
    setStorePickupOrder(prev => {
      if (!prev) return prev;
      const record = prev.paymentRecords.find(r => r.id === recordId);
      if (!record || record.isVerified) return prev;
      return { ...prev, paymentRecords: prev.paymentRecords.filter(r => r.id !== recordId) };
    });
  }, []);

  const onSessionOpened = useCallback((newSession) => {
    setSession(newSession);
    setShowSessionGate(false);
  }, []);

  const onSessionClosed = useCallback((closedSession, shopData) => {
    setSession(null);
    // If shop requires a session, show the gate so cashier must open a new one
    if ((shopData || shop)?.cashierSessionRequired) {
      setShowSessionGate(true);
    }
  }, [shop]);

  // ─── Cart helpers ───
  const addToCart = useCallback((product, variationId = null, variationName = null) => {
    playAddToCartSound();
    setCartItems(prev => {
      const key = `${product.productId}-${variationId || 'null'}`;
      const existing = prev.find(i => i.key === key);
      if (existing) {
        return prev.map(i => i.key === key ? { ...i, quantity: i.quantity + 1 } : i);
      }
      return [...prev, {
        key,
        productId: product.productId,
        variationId: variationId || null,
        // displayName may already include the variation (e.g. "Item (Volume: 100ml)") — don't repeat it
        name: product.displayName +
          (variationName && !product.displayName?.includes(variationName) ? ` — ${variationName}` : ''),
        // Plain product name + short variation values ("Volume: 100ml" → "100ml") for the cart row
        baseName: variationName
          ? product.displayName?.replace(` (${variationName})`, '')
          : product.displayName,
        variationLabel: variationName
          ? variationName.split(', ').map(part => part.split(': ').pop()).join(', ')
          : null,
        image: product.image || null,
        sku: product.sku || null,
        unitPrice: parseFloat(product.price || 0),
        quantity: 1,
        lineDiscount: 0,
      }];
    });
  }, []);

  const updateCartItem = useCallback((key, field, value) => {
    setCartItems(prev =>
      prev.map(i => i.key === key ? { ...i, [field]: parseFloat(value) || 0 } : i)
    );
  }, []);

  const removeFromCart = useCallback((key) => {
    setCartItems(prev => prev.filter(i => i.key !== key));
  }, []);

  const clearCart = useCallback(() => {
    setCartItems([]);
    setOrderDiscount(0);
    setLoyaltyPointsToRedeem(0);
    setStorePickupOrder(null);
  }, []);

  // ─── Totals ───
  const totals = (() => {
    let subtotal = 0;
    let itemDiscounts = 0;
    cartItems.forEach(i => {
      subtotal += i.quantity * i.unitPrice;
      itemDiscounts += parseFloat(i.lineDiscount) || 0;
    });
    const totalDiscount = itemDiscounts + parseFloat(orderDiscount || 0);
    const loyaltyDiscount = settings && loyaltyPointsToRedeem > 0
      ? loyaltyPointsToRedeem * parseFloat(settings.loyaltyPointValueRupees || 0)
      : 0;
    const grandTotal = Math.max(0, subtotal - totalDiscount - loyaltyDiscount);
    return { subtotal, itemDiscounts, totalDiscount, loyaltyDiscount, grandTotal };
  })();

  // ─── Submit order ───
  const submitOrder = useCallback(async (paymentDetails, status = 'completed') => {
    if (!cartItems.length) { error('Cart is empty'); return; }

    // Completing a store pickup order edits that order and dispatches it, instead of a new sale
    if (storePickupOrder) {
      if (!warehouseId) { error('Select a warehouse to dispatch from'); return; }
      try {
        setSubmitting(true);
        const result = await completeStorePickupOrderFromPos(storePickupOrder.id, {
          warehouseId,
          customerId: customer?.id || null,
          shopId: parseInt(shopId),
          sessionId: session?.id || null,
          orderDate: saleDate || null,
          backdateSessionId: backdate?.sessionId || null,
          orderItems: cartItems.map(i => ({
            id: i.orderItemId || null,
            productId: i.productId,
            variationId: i.variationId,
            quantity: i.quantity,
            unitPrice: i.unitPrice,
            lineDiscount: i.lineDiscount,
          })),
          existingPaymentRecordIds: storePickupOrder.paymentRecords.map(r => r.id),
          newPayments: paymentDetails,
        });
        setCompletedOrder(result.receipt);
        clearCart();
        setCustomer(null);
        setShowPaymentModal(false);
        setShowReceiptModal(true);
        success(`Store pickup order #${storePickupOrder.orderNumber} completed and dispatched`);
      } catch (err) {
        error(err?.response?.data?.message || 'Failed to complete store pickup order');
      } finally {
        setSubmitting(false);
      }
      return;
    }

    try {
      setSubmitting(true);
      const orderItems = cartItems.map(i => ({
        productId: i.productId,
        variationId: i.variationId,
        quantity: i.quantity,
        unitPrice: i.unitPrice,
        lineDiscount: i.lineDiscount,
      }));

      const payload = {
        shopId: parseInt(shopId),
        customerId: customer?.id || null, // backend will use Cash Sales if null — we must pass a valid ID
        warehouseId: warehouseId || null,
        sessionId: session?.id || null,
        orderDate: saleDate || null,
        backdateSessionId: backdate?.sessionId || null,
        orderItems,
        orderDiscount: parseFloat(orderDiscount) || 0,
        paymentDetails,
        loyaltyPointsToRedeem,
        orderStatusKey: status === 'credit' ? 'offline_credit' : 'offline_completed',
      };

      // If no customer selected, omit customerId and let the backend figure out Cash Sales
      // Actually backend requires customerId — we need to pass the Cash Sales ID
      // cashSalesCustomer should be pre-loaded; if not, backend will error
      if (!payload.customerId && cashSalesCustomer) {
        payload.customerId = cashSalesCustomer.id;
      }

      const result = await createQuickSaleOrder(payload);
      setCompletedOrder(result.order);
      clearCart();
      setCustomer(null);
      setShowPaymentModal(false);
      setShowReceiptModal(true);
      success('Sale completed successfully');
    } catch (err) {
      error(err?.response?.data?.message || 'Failed to complete order');
    } finally {
      setSubmitting(false);
    }
  }, [cartItems, shopId, customer, warehouseId, session, orderDiscount, loyaltyPointsToRedeem, cashSalesCustomer, storePickupOrder, backdate, clearCart, error, success]);

  const holdOrder = useCallback(() => {
    if (!cartItems.length) { error('Cart is empty'); return; }
    const held = {
      id: Date.now(),
      label: `Hold #${Date.now().toString().slice(-4)}`,
      customer,
      cartItems: [...cartItems],
      orderDiscount,
      loyaltyPointsToRedeem,
      storePickupOrder,
      grandTotal: totals.grandTotal,
      itemCount: cartItems.length,
      heldAt: new Date().toISOString(),
    };
    setHoldOrders(prev => [...prev, held]);
    clearCart();
    setCustomer(null);
  }, [cartItems, customer, orderDiscount, loyaltyPointsToRedeem, storePickupOrder, totals.grandTotal, clearCart]);

  const recallHoldOrder = useCallback((held) => {
    setCartItems(held.cartItems);
    setOrderDiscount(held.orderDiscount);
    setLoyaltyPointsToRedeem(held.loyaltyPointsToRedeem);
    setStorePickupOrder(held.storePickupOrder || null);
    setCustomer(held.customer);
    setHoldOrders(prev => prev.filter(o => o.id !== held.id));
    setShowHoldDrawer(false);
  }, []);

  return {
    // data
    shop, session, settings, paymentMethods, customer, cashSalesCustomer, warehouses, warehouseId, loading, submitting, smsEnabled,
    cartItems, orderDiscount, loyaltyPointsToRedeem, totals, completedOrder, holdOrders, storePickupOrder, saleDate, backdate,
    // modal flags
    showPaymentModal, showDiscountModal, showLoyaltyModal,
    showCustomerModal, showHoldDrawer, showReceiptModal, showSessionGate,
    // setters
    setCustomer, setBackdate, setOrderDiscount, setLoyaltyPointsToRedeem, setCashSalesCustomer, setWarehouseId,
    setShowPaymentModal, setShowDiscountModal, setShowLoyaltyModal,
    setShowCustomerModal, setShowHoldDrawer, setShowReceiptModal,
    onSessionOpened, onSessionClosed,
    // cart
    addToCart, updateCartItem, removeFromCart, clearCart,
    // actions
    submitOrder, holdOrder, recallHoldOrder, removeStorePickupPayment,
  };
};

export default useOfflineSalesTerminal;
